import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tray, Spinner, WarningCircle, CaretRight } from '@phosphor-icons/react';
import { sharedGet } from '@/lib/sharedGet';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * UX-2.5A · 作業トレイ — the single global owner of background tasks.
 * Replaces per-feature floating pills. Calm neutral presentation; the warning
 * treatment is reserved for failed or blocked work.
 */
export default function TaskTray() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      setData(await sharedGet(`${API}/api/dms/tasks/summary`, { headers: authHeader(), ttl: 3000 }));
    } catch { setData(null); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timer.current = setInterval(load, 20000);
    return () => clearInterval(timer.current);
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); btnRef.current?.focus(); }
    };
    const onClick = (e) => {
      if (!popRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const total = (data?.groups || []).reduce((n, g) => n + (g.count || 0), 0);
  if (!data || total === 0) return null;

  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <div className="relative">
      <button ref={btnRef} type="button" onClick={() => setOpen((v) => !v)}
        aria-label={`作業トレイ — 確認が必要な作業 ${total}件`}
        aria-expanded={open} aria-haspopup="true" title="作業トレイ"
        data-testid="task-tray-btn"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg text-zinc-600 hover:bg-zinc-100 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none transition-colors">
        <Tray size={18} weight="duotone" />
        <span data-testid="task-tray-count"
          className={`absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] rounded-full px-1 text-[10px] font-bold leading-[17px] tabular-nums text-white ${
            data.attention ? 'bg-rose-600' : 'bg-zinc-700'}`}>
          {total > 99 ? '99+' : total}
        </span>
      </button>

      {open && (
        <div ref={popRef} role="dialog" aria-label="作業トレイ"
          data-testid="task-tray-popover"
          className="absolute right-0 mt-2 w-80 rounded-xl border border-zinc-200 bg-white shadow-lg z-50 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-zinc-100">
            <p className="text-sm font-semibold text-zinc-900">作業トレイ</p>
            <p className="text-xs text-zinc-500 mt-0.5">確認や処理が必要な作業をまとめて表示します</p>
          </div>
          <ul className="divide-y divide-zinc-100">
            {data.groups.map((g) => (
              <li key={g.key}>
                <button type="button" onClick={() => go(g.open_path)}
                  data-testid={`task-tray-item-${g.key}`}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:outline-none transition-colors">
                  {g.tone === 'warning'
                    ? <WarningCircle size={17} weight="duotone" className="text-rose-600 shrink-0" />
                    : g.status_ja === '読み取り中'
                      ? <Spinner size={16} className="animate-spin text-zinc-400 shrink-0" />
                      : <Tray size={17} weight="duotone" className="text-zinc-400 shrink-0" />}
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-zinc-900">{g.type_ja}</span>
                    <span className="block text-xs text-zinc-500"
                      data-testid={`task-tray-label-${g.key}`}>
                      {g.label_ja || `${g.status_ja} · ${g.count}件`}
                    </span>
                  </span>
                  <CaretRight size={14} className="text-zinc-300 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
